import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { payrollClosureService } from '../services/payrollClosureService';
import './PayrollList.css';

const PayrollList = ({ payrolls, onEdit, onDelete, onCalculate, onValidate, onMarkAsPaid, onDownloadPdf, onSendEmail }) => {
  const { hasModuleAccess } = useAuth();
  const hasPayrollAccess = hasModuleAccess('PAYROLLS');
  const [closedPeriods, setClosedPeriods] = useState(new Set());

  // Vérifier les périodes clôturées pour toutes les fiches de paie
  useEffect(() => {
    const checkPeriods = async () => {
      const periodsToCheck = new Set();
      payrolls.forEach(payroll => {
        if (payroll.periodYear && payroll.periodMonth) {
          periodsToCheck.add(`${payroll.periodYear}-${payroll.periodMonth}`);
        }
      });

      const closedSet = new Set();
      for (const periodKey of periodsToCheck) {
        const [year, month] = periodKey.split('-');
        try {
          const response = await payrollClosureService.checkPeriod(parseInt(year), parseInt(month));
          if (response.data.isClosed) {
            closedSet.add(periodKey);
          }
        } catch (err) {
          console.error('Error checking period closure:', err);
        }
      }
      setClosedPeriods(closedSet);
    };

    if (payrolls.length > 0) {
      checkPeriods();
    }
  }, [payrolls]);

  const isPeriodClosed = (year, month) => {
    if (!year || !month) return false;
    return closedPeriods.has(`${year}-${month}`);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0,000 TND';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { label: 'Brouillon', class: 'status-draft' },
      CALCULATED: { label: 'Calculé', class: 'status-calculated' },
      VALIDATED: { label: 'Validé', class: 'status-validated' },
      PAID: { label: 'Payé', class: 'status-paid' },
      CANCELLED: { label: 'Annulé', class: 'status-cancelled' },
    };

    const config = statusConfig[status] || statusConfig.DRAFT;
    return (
      <span className={`status-badge ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const formatPeriod = (year, month) => {
    if (!year || !month) return '-';
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  if (payrolls.length === 0) {
    return (
      <div className="empty-state">
        <p>Aucune paie enregistrée</p>
      </div>
    );
  }

  return (
    <div className="payroll-list">
      <div className="table-container">
        <table className="payroll-table">
          <thead>
            <tr>
              <th>Employé</th>
              <th>Période</th>
              <th>Salaire de base</th>
              <th>Primes</th>
              <th>Retenues</th>
              <th>Salaire brut</th>
                <th>Salaire net</th>
              <th>Statut</th>
              {hasPayrollAccess && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {payrolls.map((payroll) => (
              <tr key={payroll.id}>
                <td>{payroll.employeeName}</td>
                <td>{formatPeriod(payroll.periodYear, payroll.periodMonth)}</td>
                <td className="amount">{formatCurrency(payroll.baseSalary)}</td>
                <td className="amount">{formatCurrency(payroll.bonuses)}</td>
                <td className="amount deductions">{formatCurrency(payroll.deductions)}</td>
                <td className="amount gross">{formatCurrency(payroll.grossSalary)}</td>
                <td className="amount net">{formatCurrency(payroll.netSalary)}</td>
                <td>
                  {getStatusBadge(payroll.status)}
                  {isPeriodClosed(payroll.periodYear, payroll.periodMonth) && (
                    <span className="status-badge status-closed" style={{ marginLeft: '5px' }}>
                      CLÔTURÉ
                    </span>
                  )}
                </td>
                {hasPayrollAccess && (
                  <td>
                    <div className="action-buttons">
                      {(payroll.status === 'VALIDATED' || payroll.status === 'PAID') && (
                        <button
                          onClick={() => onDownloadPdf(payroll.id)}
                          className="btn-action btn-download"
                          title="Télécharger PDF"
                        >
                          📄
                        </button>
                      )}
                      {!isPeriodClosed(payroll.periodYear, payroll.periodMonth) && (
                            <>
                              {payroll.status === 'DRAFT' && (
                                <>
                                  <button
                                    onClick={() => onCalculate(payroll.id)}
                                    className="btn-action btn-calculate"
                                    title="Calculer"
                                  >
                                    🧮
                                  </button>
                                  <button
                                    onClick={() => onEdit(payroll)}
                                    className="btn-action btn-edit"
                                    title="Modifier"
                                  >
                                    ✏️
                                  </button>
                                </>
                              )}
                              {payroll.status === 'CALCULATED' && (
                                <button
                                  onClick={() => onValidate(payroll.id)}
                                  className="btn-action btn-validate"
                                  title="Valider"
                                >
                                  ✓
                                </button>
                              )}
                              {payroll.status === 'VALIDATED' && (
                                <>
                                  <button
                                    onClick={() => onMarkAsPaid(payroll.id)}
                                    className="btn-action btn-paid"
                                    title="Marquer comme payé"
                                  >
                                    💰
                                  </button>
                                  <button
                                    onClick={() => onSendEmail(payroll.id)}
                                    className="btn-action btn-email"
                                    title="Envoyer par email"
                                  >
                                    📧
                                  </button>
                                </>
                              )}
                              {payroll.status !== 'PAID' && (
                                <button
                                  onClick={() => onDelete(payroll.id)}
                                  className="btn-action btn-delete"
                                  title="Supprimer"
                                >
                                  🗑️
                                </button>
                              )}
                            </>
                          )}
                          {(payroll.status === 'PAID' || isPeriodClosed(payroll.periodYear, payroll.periodMonth)) && (
                            <button
                              onClick={() => onDownloadPdf(payroll.id)}
                              className="btn-action btn-download"
                              title="Télécharger PDF"
                            >
                              📄
                            </button>
                          )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayrollList;

